import React, { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import {
  Select, SelectTrigger, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  COUNTRY_DIAL, DEFAULT_COUNTRY, dialCountries, splitMobile, countryForDial,
} from '@/lib/countries';

/**
 * Country code picker + national number, emitting one E.164-style string
 * ("+919876543210"). The code defaults to the profile/listing country and
 * falls back to India. A stored legacy number without a code is normalised as
 * soon as it arrives — including values that load asynchronously after mount —
 * so saving the form never drops the prefix.
 *
 * Select values are COUNTRY names, not dial codes: USA and Canada share +1 and
 * Radix needs unique item values.
 */
const MobileInput = ({ value, onChange, country, disabled, testid = 'mobile' }) => {
  const fallbackCountry = (COUNTRY_DIAL[country] && country) || DEFAULT_COUNTRY;
  const [dialCountry, setDialCountry] = useState(
    () => countryForDial(splitMobile(value).dial) || fallbackCountry,
  );

  const parsed = splitMobile(value);

  // External value already carries a code → follow it. A legacy value without
  // one gets rewritten (runs on every value change, because on Post Listing
  // the stored number is prefilled well after mount).
  useEffect(() => {
    const p = splitMobile(value);
    if (p.dial) {
      // Compare CODES, not names: USA/Canada share +1, so comparing names
      // would flip an explicit Canada pick back to USA on every keystroke.
      if (COUNTRY_DIAL[dialCountry].code !== p.dial) {
        const c = countryForDial(p.dial);
        if (c) setDialCountry(c);
      }
    } else if (p.national) {
      onChange(`${COUNTRY_DIAL[dialCountry].code}${p.national}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Country changed and no number typed yet → adopt that country's code.
  useEffect(() => {
    if (!parsed.dial && country && COUNTRY_DIAL[country]) setDialCountry(country);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  const dial = COUNTRY_DIAL[dialCountry].code;
  const emit = (d, n) => onChange(n ? `${d}${n}` : '');

  return (
    <div className='flex gap-2'>
      <Select
        value={dialCountry}
        onValueChange={(c) => { setDialCountry(c); emit(COUNTRY_DIAL[c].code, parsed.national); }}
        disabled={disabled}
      >
        <SelectTrigger className='w-[108px] shrink-0' data-testid={`${testid}-dial`}>
          <span className='tabular-nums'>{dial}</span>
        </SelectTrigger>
        <SelectContent className='max-h-72'>
          {dialCountries().map(c => (
            <SelectItem key={c} value={c}>
              {COUNTRY_DIAL[c].code} · {c}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        className='min-w-0'
        inputMode='tel'
        value={parsed.national}
        disabled={disabled}
        placeholder='Mobile number'
        onChange={(e) => emit(dial, e.target.value.replace(/\D/g, ''))}
        data-testid={`${testid}-number`}
      />
    </div>
  );
};

export default MobileInput;
